import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Drawer, Grid, styled, Typography } from "@mui/material";
import { DrawerProps } from "@mui/material/Drawer";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcRequestIndicatorType } from "components/MprcRequestIndicator";
import { MprcRequestIndicatorWrapper } from "components/MprcRequestIndicatorWrapper";
import * as React from "react";
import { useTranslation } from "react-i18next";

export type BoxDrawerExitMode = "backdrop" | "escape" | "exit";

interface IProps {
    open?: boolean;
    closeIcon?: IconProp;
    children?: React.ReactNode;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    content?: React.ReactNode;
    width?: "string" | number;
    requestIndicator?: MprcRequestIndicatorType;
    footerFullWidth?: boolean;
    disableScroll?: boolean;
    onClose: (exitMode: BoxDrawerExitMode) => void;
}

type Props = IProps & Omit<DrawerProps, "anchor" | "title" | "onBackdropClick" | "onEscapeKeyDown" | "content" | "onClose">;


const StyledDrawer = styled(Drawer)(
    ({ theme }) => ({
        position: "relative",
        "& > .MuiPaper-root": {
            ...theme.extended.workArea.rightDrawer,
        }
    })
);

export const MprcBoxDrawer: React.FC<Props> = (props) => {

    const { t } = useTranslation("shared");
    const { children, open, width, closeIcon, header, footer, content, title, subtitle, requestIndicator, footerFullWidth, disableScroll, onClose, ...rest } = props;

    const hasTitleLine = title || subtitle;

    return (
        <StyledDrawer
            open={open ?? true}
            anchor="right"
            onClose={(e, reason) => onClose(reason == "escapeKeyDown" ? "escape" : "backdrop")}
            sx={{ width }}
            {...rest}
        >
            <MprcRequestIndicatorWrapper requestIndicator={requestIndicator}>
                <Grid
                    container
                    direction="column"
                    wrap="nowrap"
                    sx={
                        (theme) => (
                            {
                                padding: theme.spacing(1),
                                height: "100%",
                                overflow: "hidden",
                                position: "relative"
                            })
                    }>
                    <Grid
                        sx={
                            (theme) => (
                                {
                                    backgroundColor: theme.palette.common.white,
                                    marginBottom: theme.spacing(),
                                    paddingRight: 0
                                })
                        }
                    >
                        <Grid container wrap="nowrap" justifyContent="space-between" alignItems="start" sx={{ pl: hasTitleLine ? 0 : 1, pr: hasTitleLine ? 0 : 1 }} >
                            {hasTitleLine &&
                                <Grid size="grow">
                                    <Typography
                                        component="div"
                                        noWrap={true}
                                        variant="h2"
                                        color="muted.main"
                                        title={typeof (title) == "string" ? title : undefined}
                                    >
                                        {title}
                                    </Typography>
                                    {
                                        subtitle &&
                                        <Typography component="div" noWrap={true} variant="caption" color="muted.main" title={typeof (subtitle) == "string" ? subtitle : undefined}>{subtitle}</Typography>
                                    }
                                </Grid>
                            }
                            <Grid>
                                <Typography
                                    component="div"
                                    variant="h2"
                                    color="muted.main"
                                    title={t("Literal.Close")}
                                >
                                    <FontAwesomeIcon icon={closeIcon ?? faXmark}
                                        fontSize="20px"
                                        className={AppGlobalClassKeys.clickable}
                                        onClick={() => onClose("exit")}
                                    />
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid
                        size="grow"
                        sx={{ position: "relative" }}
                    >

                        <Grid
                            container
                            direction="column"
                            wrap="nowrap"
                            sx={
                                {
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                }
                            }
                        >
                            {
                                header &&
                                <Grid width="100%">
                                    {header}
                                </Grid>
                            }

                            <Grid
                                size="grow"
                                width="100%"
                                sx={
                                    {
                                        overflowX: "hidden",
                                        overflowY: disableScroll ? "hidden" : "auto",
                                        mt: 1
                                    }
                                }
                                className={disableScroll ? undefined : AppGlobalClassKeys.thinScrollbar}
                            >
                                {content ?? children}
                            </Grid>

                            {
                                footer &&
                                <Grid width={footerFullWidth ? "100%" : "inherit"} alignSelf="center" justifySelf="center" sx={{ paddingTop: .5 }}>
                                    {footer}
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </MprcRequestIndicatorWrapper>
        </StyledDrawer >
    );
};
